/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================
 
* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import Grid from "@mui/material/Grid";
import Tooltip from "@mui/material/Tooltip";
import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 3 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import ReportsBarChart from "examples/Charts/BarCharts/ReportsBarChart";
import ReportsLineChart from "examples/Charts/LineCharts/ReportsLineChart";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";
import BookingCard from "examples/Cards/BookingCard";

// Anaytics dashboard components
import SalesByCountry from "layouts/dashboards/analytics/components/SalesByCountry";

// Data
import reportsBarChartData from "layouts/dashboards/analytics/data/reportsBarChartData";
import reportsLineChartData from "layouts/dashboards/analytics/data/reportsLineChartData";

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

// Images
import eoCover from "assets/images/eo_cover.png";
import eoCover2 from "assets/images/eocover2.png";
import eoCover3 from "assets/images/eocover3.png";
import eoCover4 from "assets/images/eocover4.png";
import eo_mini1 from "assets/images/eo_mini1.png";
import eo_hp4 from "assets/images/eo_hp4.png";
import eo_hp3 from "assets/images/eo_hp3.png";
import eo_hp2 from "assets/images/eo_hp2.png";

function Analytics() {
  const { sales, tasks } = reportsLineChartData;
  const [berita, setBerita] = useState([]);
  const [loadingBerita, setLoadingBerita] = useState(false);
  const [errorBerita, setErrorBerita] = useState("");
  const [openBeritaModal, setOpenBeritaModal] = useState(false);
  const [selectedBerita, setSelectedBerita] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Array gambar untuk slider - desktop dan mobile
  const coverImagesDesktop = [eoCover, eoCover2, eoCover3, eoCover4];
  const coverImagesMobile = [eo_mini1, eo_hp2, eo_hp3, eo_hp4];
  const coverImages = isMobile ? coverImagesMobile : coverImagesDesktop;

  // Deteksi ukuran layar untuk mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 960); // md breakpoint biasanya 960px
      // Reset index ketika switch antara mobile dan desktop
      setCurrentImageIndex(0);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Auto-slide banner
  useEffect(() => {
    const imageCount = coverImages.length;
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % imageCount);
    }, 4000); // Change image every 4 seconds

    return () => clearInterval(interval);
  }, [isMobile]);

  // Action buttons for the BookingCard
  const actionButtons = (
    <>
      <Tooltip title="Refresh" placement="bottom">
        <MDTypography
          variant="body1"
          color="primary"
          lineHeight={1}
          sx={{ cursor: "pointer", mx: 3 }}
        >
          <Icon color="inherit">refresh</Icon>
        </MDTypography>
      </Tooltip>
      <Tooltip title="Edit" placement="bottom">
        <MDTypography
          variant="body1"
          color="info"
          lineHeight={1}
          sx={{ cursor: "pointer", mx: 3 }}
        >
          <Icon color="inherit">edit</Icon>
        </MDTypography>
      </Tooltip>
    </>
  );

  useEffect(() => {
    const fetchBerita = async () => {
      setLoadingBerita(true);
      setErrorBerita("");
      try {
        const res = await axios.get(
          "https://smartservice.rotendaokab.go.id/api/beritaPortal"
        );
        const list = Array.isArray(res.data?.data) ? res.data.data : [];
        setBerita(list);
      } catch (e) {
        setErrorBerita("Gagal memuat berita.");
        setBerita([]);
      } finally {
        setLoadingBerita(false);
      }
    };
    fetchBerita();
  }, []);

  const handleOpenBerita = (item) => {
    setSelectedBerita(item);
    setOpenBeritaModal(true);
  };

  const handleCloseBerita = () => {
    setOpenBeritaModal(false);
    setSelectedBerita(null);
  };

  const beritaCards = useMemo(() => {
    if (!berita || berita.length === 0) return null;
    return berita.slice(0, 20).map((item) => {
      const images = (item.gambar || "")
        .split(",")
        .map((g) => g.trim())
        .filter(Boolean);
      const mainImage = images[0] || undefined;
      const plainText = String(item.isi || "")
        .replace(/\r?\n/g, " ")
        .trim();
      const shortDesc =
        plainText.length > 160 ? `${plainText.slice(0, 160)}…` : plainText;

      const cardAction = (
        <MDTypography
          variant="body1"
          color="info"
          lineHeight={1}
          sx={{ cursor: "pointer", mx: 1 }}
          onClick={() => handleOpenBerita(item)}
        >
          <Icon color="inherit">visibility</Icon>
        </MDTypography>
      );

      return (
        <Grid item xs={12} md={6} lg={4} key={item.id}>
          <MDBox mt={3}>
            <BookingCard
              image={mainImage}
              title={item.judul}
              description={shortDesc}
              price={item.kategori || ""}
              location={item.tanggal || ""}
              action={cardAction}
            />
          </MDBox>
        </Grid>
      );
    });
  }, [berita]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pb={3}>
        {/* Banner Header Slider */}
        <MDBox
          mb={3}
          sx={{
            position: "relative",
            width: "100%",
            height: "200px",
            overflow: "hidden",
            borderRadius: "xl",
          }}
        >
          {coverImages.map((image, index) => (
            <MDBox
              key={index}
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                opacity: index === currentImageIndex ? 1 : 0,
                transition: "opacity 1s ease-in-out",
              }}
            >
              <img
                src={image}
                alt={`ESA Office Banner ${index + 1}`}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            </MDBox>
          ))}
        </MDBox>
        {/* Berita (Sumber: DKISP Rote Ndao) */}
        <MDBox mt={3}>
          <MDTypography variant="h5" fontWeight="bold" mb={1} ml={1}>
            Informasi Terbaru
          </MDTypography>
          <MDTypography variant="button" color="text" ml={1}>
            Sumber: DKISP Rote Ndao – Portal Berita (
            <a
              href="https://smartservice.rotendaokab.go.id/api/beritaPortal"
              target="_blank"
              rel="noreferrer"
            >
              tautan
            </a>
            )
          </MDTypography>
          <Grid container spacing={3}>
            {loadingBerita && (
              <Grid item xs={12}>
                <MDBox display="flex" justifyContent="center" py={4}>
                  <CircularProgress size={32} />
                </MDBox>
              </Grid>
            )}
            {errorBerita && !loadingBerita && (
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <MDTypography color="error">{errorBerita}</MDTypography>
                  </CardContent>
                </Card>
              </Grid>
            )}
            {!loadingBerita && !errorBerita && beritaCards}
            {!loadingBerita &&
              !errorBerita &&
              (!berita || berita.length === 0) && (
                <Grid item xs={12}>
                  <MDBox textAlign="center" py={4}>
                    <Icon sx={{ fontSize: 40, color: "text.secondary" }}>
                      article
                    </Icon>
                    <MDTypography variant="button" color="text">
                      Belum ada berita untuk ditampilkan
                    </MDTypography>
                  </MDBox>
                </Grid>
              )}
          </Grid>
        </MDBox>

        {/* Modal Detail Berita */}
        <Dialog
          open={openBeritaModal}
          onClose={handleCloseBerita}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <MDTypography variant="h5">
                {selectedBerita?.judul || "Detail Berita"}
              </MDTypography>
              <Button
                onClick={handleCloseBerita}
                variant="outlined"
                color="secondary"
                size="small"
              >
                Tutup
              </Button>
            </MDBox>
          </DialogTitle>
          <DialogContent>
            {selectedBerita && (
              <MDBox>
                <MDTypography variant="button" color="text" gutterBottom>
                  {selectedBerita.kategori || ""} •{" "}
                  {selectedBerita.tanggal || ""}
                </MDTypography>
                <Divider sx={{ my: 2 }} />
                <Grid container spacing={2}>
                  {(selectedBerita.gambar || "")
                    .split(",")
                    .map((g) => g.trim())
                    .filter(Boolean)
                    .slice(0, 3)
                    .map((img, idx) => (
                      <Grid
                        item
                        xs={12}
                        md={idx === 0 ? 12 : 6}
                        key={`${selectedBerita.id}-${idx}`}
                      >
                        <MDBox
                          component="img"
                          src={img}
                          alt={`gambar-${idx}`}
                          sx={{
                            width: "100%",
                            height: idx === 0 ? 300 : 180,
                            objectFit: "cover",
                            borderRadius: 2,
                            border: "1px solid #eee",
                          }}
                        />
                      </Grid>
                    ))}
                </Grid>
                <MDBox mt={2}>
                  <MDTypography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                    {String(selectedBerita.isi || "").trim()}
                  </MDTypography>
                </MDBox>
              </MDBox>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseBerita} color="primary">
              Tutup
            </Button>
          </DialogActions>
        </Dialog>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Analytics;
